import { getDefaultAcwTimeout } from '../../../../utils/config';
import { CXoneEventsSubscriptionContext } from '../../subscription-context';

export interface AgentStateEvent {
  currentState?: {
    state?: string;
    reason?: string;
    cxoneState?: string;
    acwTimeout?: number;
    startTime?: number;
    isACW?: boolean | string;
  };
  agentStateData?: Record<string, string | number>;
}

function resolveCurrentStateName(eventObj: AgentStateEvent): string {
  return (eventObj.currentState?.state || eventObj.currentState?.cxoneState || '').trim();
}

function isAgentOnContact(stateName: string): boolean {
  const stateLower = stateName.toLowerCase();
  return (
    stateLower.includes('contact') ||
    stateLower === 'active' ||
    stateLower === 'inbound' ||
    stateLower === 'outboundcontact'
  );
}

function resolveIsInAcw(eventObj: AgentStateEvent): boolean {
  const stateName = resolveCurrentStateName(eventObj);
  const stateLower = stateName.toLowerCase();

  if (stateLower === 'available' || isAgentOnContact(stateName)) {
    return false;
  }

  const currentStateAcw = eventObj.currentState?.isACW;
  if (currentStateAcw !== undefined) {
    return parseBooleanFlag(
      typeof currentStateAcw === 'boolean' ? String(currentStateAcw) : currentStateAcw
    );
  }

  const agentStateData = eventObj.agentStateData;
  const isAcwFromData = parseBooleanFlag(agentStateData?.IsAcw || agentStateData?.isAcw);
  return stateLower === 'unavailable' && isAcwFromData;
}

export function parseAgentStateEvent(event: unknown): AgentStateEvent | null {
  if (!event || typeof event !== 'object') {
    return null;
  }
  return event as AgentStateEvent;
}

export function parseBooleanFlag(value: string | number | undefined): boolean {
  return value === 'True' || value === 'true' || String(value) === 'true';
}

export function applyAcwStateFromEvent(
  eventObj: AgentStateEvent,
  ctx: CXoneEventsSubscriptionContext
): void {
  const currentStateAcwTimeout = eventObj.currentState?.acwTimeout;
  const agentStateData = eventObj.agentStateData;
  const isInAcwNow = resolveIsInAcw(eventObj);

  const rawAcwTimeout = agentStateData?.AcwTimeout || agentStateData?.acwTimeout;
  const acwTimeoutFromData = rawAcwTimeout ? Number(rawAcwTimeout) : 0;

  const requireDispositionFromData = parseBooleanFlag(
    agentStateData?.RequireDisposition || agentStateData?.requireDisposition
  );

  if (isInAcwNow) {
    const timeout = currentStateAcwTimeout || acwTimeoutFromData || getDefaultAcwTimeout();
    ctx.setAcwTimeout(timeout);
    ctx.setIsInAcw(true);
    ctx.setCallState((prev) => ({
      ...prev,
      requireDisposition: requireDispositionFromData,
    }));
    return;
  }

  if (currentStateAcwTimeout && currentStateAcwTimeout > 0) {
    ctx.setAcwTimeout(currentStateAcwTimeout);
  } else if (acwTimeoutFromData > 0) {
    ctx.setAcwTimeout(acwTimeoutFromData);
  }
  ctx.setIsInAcw(false);
}

export function resolveAgentReason(
  eventObj: AgentStateEvent,
  unavailableCodes: { outStateId: string; outStateName: string }[]
): { reasonName: string | null; reasonCode: string | null } {
  const currentState = eventObj.currentState;
  const agentStateData = eventObj.agentStateData;
  const outStateId =
    agentStateData?.outStateId ??
    agentStateData?.OutStateId ??
    agentStateData?.outStateCode ??
    agentStateData?.OutStateCode;

  const sdkReason = currentState?.reason;
  const currentOutReason = agentStateData?.CurrentOutReason as string | undefined;

  let reasonName =
    (sdkReason && sdkReason.trim() !== '' ? sdkReason : null) ||
    (currentOutReason && currentOutReason.trim() !== '' ? currentOutReason : null) ||
    (agentStateData?.outStateName as string) ||
    (agentStateData?.OutStateName as string) ||
    null;

  const reasonCode = outStateId !== undefined ? String(outStateId) : null;

  if (!reasonName && outStateId !== undefined) {
    const matchingCode = unavailableCodes.find((code) => code.outStateId === String(outStateId));
    if (matchingCode) {
      reasonName = matchingCode.outStateName;
    }
  }

  return { reasonName, reasonCode };
}
